// The company admin console: overview and plan, people, billing (Stripe), single sign-on, branding and settings,
// and the audit log. Data: GET /api/admin.
import { useEffect, useState } from "react";
import { Link, NavLink, Route, Routes, useSearchParams } from "react-router-dom";
import { Building2, Check, CheckCircle2, Circle, CreditCard, ImageUp, KeyRound, Mail, Palette, ScrollText, Trash2, UserPlus, Users } from "lucide-react";
import { useMe, useReloadMe } from "../App";
import { api, useApi } from "../lib/api";
import { fullDate, money, ROLE_LABEL, shortDate, when } from "../lib/format";
import type { AdminOverview, Member, Plan, PlanId, Role } from "../lib/types";
import { Card, Chip, inputCls } from "../components/bits";
import { Button, ConfirmDialog, cx, Empty, ErrorNote, Spinner, toast } from "../components/ui";
import { CompanyMark } from "../components/icons";
import { TimezoneSelect } from "../sched/components/TimezoneSelect";
import CompanySso from "./CompanySso";

const TABS = [
  { to: "/company", label: "Overview", icon: Building2, end: true },
  { to: "/company/people", label: "People", icon: Users },
  { to: "/company/billing", label: "Plan & billing", icon: CreditCard },
  { to: "/company/sso", label: "Single sign-on", icon: KeyRound },
  { to: "/company/branding", label: "Branding & settings", icon: Palette },
  { to: "/company/audit", label: "Audit log", icon: ScrollText },
];

export default function CompanyAdmin() {
  const { data, error, loading, reload } = useApi<AdminOverview>("/admin");
  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-xl font-semibold">{data?.company.name ?? "Company"}</h1>
        {data && data.company.status !== "active" && <Chip tone="bad">Paused</Chip>}
        {data && <Chip tone="accent">{data.onTrial ? `${data.plan.name} trial` : `${data.plan.name} plan`}</Chip>}
      </div>
      <nav className="flex gap-1 overflow-x-auto -mx-1 px-1 mb-5 [scrollbar-width:none] border-b border-line" aria-label="Company">
        {TABS.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => cx("inline-flex items-center gap-1.5 px-3 h-10 text-sm font-medium whitespace-nowrap border-b-2 -mb-px", isActive ? "border-accent text-accent-text" : "border-transparent text-ink-2 hover:text-ink")}>
            <t.icon className="size-4" /> {t.label}
          </NavLink>
        ))}
      </nav>
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner />
      ) : data ? (
        <Routes>
          <Route index element={<Overview o={data} />} />
          <Route path="people" element={<People o={data} reload={reload} />} />
          <Route path="billing" element={<Billing o={data} reload={reload} />} />
          <Route path="sso" element={<CompanySso o={data} reload={reload} />} />
          <Route path="branding" element={<Branding o={data} reload={reload} />} />
          <Route path="audit" element={<Audit />} />
        </Routes>
      ) : null}
    </div>
  );
}

function Meter({ label, used, limit }: { label: string; used: number; limit: number | null }) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div className="bg-surface border border-line rounded-xl px-4 py-3">
      <div className="text-xs text-muted">{label}</div>
      <div className="text-2xl font-semibold tabular">
        {used}
        <span className="text-sm text-muted font-normal"> / {limit ?? "unlimited"}</span>
      </div>
      {limit !== null && (
        <div className="h-1.5 rounded-full bg-surface-2 mt-2 overflow-hidden" aria-hidden>
          <div className={cx("h-full rounded-full", pct >= 90 ? "bg-bad" : pct >= 70 ? "bg-warn" : "bg-accent")} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

/** What a plan includes, one line each. */
function planLines(p: Plan) {
  return [
    p.users === null ? "Unlimited people" : `${p.users} ${p.users === 1 ? "person" : "people"}`,
    p.links === null ? "Unlimited booking links" : `${p.links} booking link`,
    p.connections === null ? "Unlimited calendars per person" : `${p.connections} calendar connection`,
    p.advanced ? "Several meeting lengths per link, reminder emails" : "One meeting length per link",
    p.branding ? "Your logo and colour on booking pages and emails" : "eBookMe branding",
    p.groupLinks ? "Group links with co-hosts" : null,
    p.sso ? "Single sign-on (Entra ID)" : null,
  ].filter(Boolean) as string[];
}

function Overview({ o }: { o: AdminOverview }) {
  const steps = [
    { done: o.members.length > 1, text: "Invite your colleagues", to: "/company/people" },
    { done: !o.onTrial && o.company.plan !== "free", text: "Choose a plan", to: "/company/billing" },
    { done: Boolean(o.company.branding.logoPng), text: o.plan.branding ? "Add your logo for booking pages and emails" : "Your logo on booking pages (Standard and up)", to: "/company/branding" },
    { done: o.domains.some((d) => d.verifiedAt), text: "Verify your email domain", to: "/company/sso" },
    { done: o.idps.some((i) => i.testedAt), text: o.plan.sso ? "Connect your Entra ID (single sign-on)" : "Single sign-on (Teams plan)", to: "/company/sso" },
  ];
  const trialDays = o.onTrial && o.trialEndsAt ? Math.max(0, Math.ceil((Date.parse(o.trialEndsAt) - Date.now()) / 86_400_000)) : null;
  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_22rem] gap-5">
      <div className="space-y-5 min-w-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Meter label="People" used={o.seatsUsed} limit={o.plan.users} />
          <div className="bg-surface border border-line rounded-xl px-4 py-3">
            <div className="text-xs text-muted">{o.company.billing.subscriptionId ? "Paid seats" : "Plan"}</div>
            <div className="text-2xl font-semibold tabular">{o.company.billing.subscriptionId ? o.company.billing.seats ?? o.seatsUsed : o.paidPlan.name}</div>
            <div className="text-xs text-muted mt-1">{o.company.billing.currentPeriodEnd ? `Renews ${shortDate(o.company.billing.currentPeriodEnd)}` : trialDays !== null ? `Trial ends in ${trialDays} day${trialDays === 1 ? "" : "s"}` : "No subscription"}</div>
          </div>
        </div>
        <Card title="Getting set up">
          <ul className="space-y-2">
            {steps.map((s) => (
              <li key={s.text} className="flex items-center gap-2 text-sm">
                {s.done ? <CheckCircle2 className="size-4 text-good shrink-0" /> : <Circle className="size-4 text-muted shrink-0" />}
                {s.to && !s.done ? (
                  <Link to={s.to} className="text-accent-text hover:underline">
                    {s.text}
                  </Link>
                ) : (
                  <span className={s.done ? "text-ink-2" : "text-ink"}>{s.text}</span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <Card title={o.onTrial ? `${o.plan.name} trial` : "Your plan"}>
        <div className="text-sm space-y-1.5">
          <div className="text-lg font-semibold">{o.plan.name}</div>
          {planLines(o.plan).map((l) => (
            <div key={l}>{l}</div>
          ))}
          <p className="text-xs text-muted pt-2">
            <Link to="/company/billing" className="text-accent-text hover:underline">
              Plan & billing
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
}

function People({ o, reload }: { o: AdminOverview; reload: () => Promise<void> }) {
  const me = useMe();
  const [invite, setInvite] = useState({ email: "", name: "", role: "member" as Role });
  const [busy, setBusy] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Member | null>(null);
  const run = async (key: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(key);
    try {
      await fn();
      if (ok) toast(ok);
      await reload();
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(null);
    }
  };
  const full = o.plan.users !== null && o.seatsUsed >= o.plan.users;
  return (
    <div className="space-y-5">
      <Card title="Invite someone">
        {full && (
          <div className="mb-3 text-sm">
            <ErrorNote>
              The {o.plan.name} plan includes {o.plan.users} {o.plan.users === 1 ? "person" : "people"}.{" "}
              <Link to="/company/billing" className="underline">
                Choose a paid plan
              </Link>{" "}
              to add more.
            </ErrorNote>
          </div>
        )}
        <form
          className="grid sm:grid-cols-[1fr_1fr_10rem_auto] gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void run("invite", () => api("/admin/members", { body: invite }), `Invitation sent to ${invite.email}`).then(() => setInvite({ email: "", name: "", role: "member" }));
          }}
        >
          <input required type="email" placeholder="Email" aria-label="Email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className={inputCls} />
          <input placeholder="Name (optional)" aria-label="Name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} className={inputCls} />
          <select aria-label="Role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value as Role })} className={inputCls}>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
            {me.role === "owner" && <option value="owner">Owner</option>}
          </select>
          <Button variant="primary" type="submit" busy={busy === "invite"} disabled={full}>
            <UserPlus className="size-4" /> Invite
          </Button>
        </form>
        <p className="text-xs text-muted mt-2">
          Members connect their calendars and share their own booking links. Admins also manage people, billing, single sign-on and branding. Owners can also manage other owners.
          {o.company.billing.subscriptionId && " Each active person is a paid seat; adding or removing people adjusts your subscription (prorated)."}
        </p>
      </Card>
      <div className="bg-surface border border-line rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted border-b border-line">
            <tr>
              <th className="px-4 py-2 font-medium">Person</th>
              <th className="px-4 py-2 font-medium">Role</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Last seen</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {o.members.map((m) => {
              const self = m.email === me.email;
              return (
                <tr key={m.email}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-ink">{m.name || m.email}</div>
                    {m.name && <div className="text-xs text-muted">{m.email}</div>}
                  </td>
                  <td className="px-4 py-2.5">
                    <select
                      aria-label={`Role for ${m.email}`}
                      value={m.role}
                      disabled={self || busy !== null || (m.role === "owner" && me.role !== "owner")}
                      onChange={(e) => run(`role:${m.email}`, () => api("/admin/members/update", { body: { email: m.email, role: e.target.value } }), "Role changed")}
                      className={cx(inputCls, "h-8")}
                      style={{ width: "7.5rem" }}
                    >
                      {(["member", "admin", "owner"] as Role[])
                        .filter((r) => r !== "owner" || me.role === "owner" || m.role === "owner")
                        .map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABEL[r]}
                          </option>
                        ))}
                    </select>
                  </td>
                  <td className="px-4 py-2.5">{m.status === "active" ? <Chip tone="good">Active</Chip> : m.status === "invited" ? <Chip tone="accent">Invited {when(m.invitedAt)}</Chip> : <Chip tone="bad">Turned off</Chip>}</td>
                  <td className="px-4 py-2.5 text-muted whitespace-nowrap">{m.lastSeenAt ? when(m.lastSeenAt) : "—"}</td>
                  <td className="px-4 py-2.5">
                    {!self && (
                      <div className="flex justify-end gap-1.5">
                        {m.status === "invited" && (
                          <Button className="h-8" busy={busy === `resend:${m.email}`} onClick={() => run(`resend:${m.email}`, () => api("/admin/members/resend", { body: { email: m.email } }), "Invitation sent again")}>
                            <Mail className="size-3.5" /> Resend
                          </Button>
                        )}
                        <Button className="h-8" busy={busy === `status:${m.email}`} onClick={() => run(`status:${m.email}`, () => api("/admin/members/update", { body: { email: m.email, status: m.status === "disabled" ? "active" : "disabled" } }))}>
                          {m.status === "disabled" ? "Turn on" : "Turn off"}
                        </Button>
                        <button className="p-2 rounded-lg text-muted hover:bg-surface-2 hover:text-bad" aria-label={`Remove ${m.email}`} title="Remove" onClick={() => setRemoving(m)}>
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {removing && (
        <ConfirmDialog
          title={`Remove ${removing.name || removing.email}?`}
          confirm="Remove"
          danger
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            await api(`/admin/members?${new URLSearchParams({ email: removing.email })}`, { method: "DELETE" });
            toast("Removed");
            await reload();
          }}
        >
          <p>They lose access to eBookMe for {o.company.name}. Their booking links stop taking bookings; meetings already booked stay on the calendars.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

const BILLING_STATUS: Record<string, { label: string; tone: "good" | "warn" | "bad" | "muted" }> = {
  active: { label: "Active", tone: "good" },
  trialing: { label: "Trial", tone: "good" },
  past_due: { label: "Payment overdue", tone: "warn" },
  unpaid: { label: "Unpaid", tone: "bad" },
  canceled: { label: "Canceled", tone: "muted" },
  incomplete: { label: "Waiting for payment", tone: "warn" },
  incomplete_expired: { label: "Payment not completed", tone: "muted" },
};

function Billing({ o, reload }: { o: AdminOverview; reload: () => Promise<void> }) {
  const reloadMe = useReloadMe();
  const [q, setQ] = useSearchParams();
  const [busy, setBusy] = useState<string | null>(null);
  const b = o.company.billing;
  const subscribed = Boolean(b.subscriptionId && b.status && !["canceled", "incomplete_expired"].includes(b.status));
  // Back from Stripe Checkout: the webhook may land a moment after the redirect.
  useEffect(() => {
    if (q.get("checkout") !== "done") return;
    toast("Thanks! Your plan updates as soon as Stripe confirms the payment.");
    const t = setTimeout(() => void Promise.all([reload(), reloadMe()]), 2500);
    setQ({}, { replace: true });
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const go = async (key: string, path: string, body?: unknown) => {
    setBusy(key);
    try {
      const { url } = await api<{ url: string }>(path, { body: body ?? {} });
      location.assign(url);
    } catch (e) {
      toast((e as Error).message, "error");
      setBusy(null);
    }
  };
  const st = b.status ? BILLING_STATUS[b.status] ?? { label: b.status, tone: "muted" as const } : null;
  return (
    <div className="space-y-5">
      {!o.billingConfigured && <ErrorNote>Online billing isn't switched on yet. Contact eBookMe to change plans.</ErrorNote>}
      <Card title="Current plan">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-lg font-semibold">{o.plan.name}</span>
          {o.onTrial && <Chip tone="accent">Trial until {shortDate(o.trialEndsAt)}</Chip>}
          {st && <Chip tone={st.tone}>{st.label}</Chip>}
          {subscribed && (
            <span className="text-muted">
              {b.seats ?? o.seatsUsed} seat{(b.seats ?? o.seatsUsed) === 1 ? "" : "s"} · {b.cancelAtPeriodEnd ? "ends" : "renews"} {shortDate(b.currentPeriodEnd)}
            </span>
          )}
        </div>
        {subscribed ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button variant="primary" busy={busy === "portal"} disabled={!o.billingConfigured} onClick={() => go("portal", "/admin/billing/portal")}>
              Manage subscription
            </Button>
            <span className="text-xs text-muted">Change plan, update your card, download invoices or cancel, on Stripe.</span>
          </div>
        ) : (
          <p className="text-xs text-muted mt-2">
            {o.onTrial ? "When the trial ends without a plan, your company moves to Free: 1 person, 1 booking link and 1 calendar. Extra links are paused, not deleted." : "Choose a plan below. Prices are per person per year, billed yearly."}
          </p>
        )}
      </Card>
      <div className="grid md:grid-cols-3 gap-4">
        {o.plans.map((p) => {
          const current = (subscribed ? o.paidPlan.id : o.onTrial ? null : o.company.plan) === p.id;
          return (
            <section key={p.id} className={cx("bg-surface border rounded-xl p-4 flex flex-col", current ? "border-accent" : "border-line")}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{p.name}</h3>
                {current && <Chip tone="accent">Current</Chip>}
              </div>
              <div className="mt-1">
                <span className="text-2xl font-bold">{money(p.pricePerSeatYear)}</span>
                <span className="text-sm text-muted"> {p.pricePerSeatYear ? "per person / year" : "forever"}</span>
              </div>
              {p.pricePerSeatYear > 0 && <div className="text-xs text-muted">{`${money(Math.round((p.pricePerSeatYear / 12) * 100) / 100)} a month, billed yearly`}</div>}
              <ul className="mt-3 space-y-1.5 text-sm flex-1">
                {planLines(p).map((l) => (
                  <li key={l} className="flex gap-1.5">
                    <Check className="size-4 text-good shrink-0 mt-0.5" /> {l}
                  </li>
                ))}
              </ul>
              {p.id !== "free" && !current && (
                <Button variant={p.id === "standard" ? "primary" : "secondary"} className="mt-4 w-full" busy={busy === p.id} disabled={!o.billingConfigured} onClick={() => go(p.id, subscribed ? "/admin/billing/portal" : "/admin/billing/checkout", { plan: p.id as PlanId })}>
                  {subscribed ? `Switch to ${p.name}` : `Choose ${p.name}`}
                </Button>
              )}
              {p.id !== "free" && !subscribed && <p className="text-xs text-muted mt-2 text-center">{o.seatsUsed} seat{o.seatsUsed === 1 ? "" : "s"} · {money(p.pricePerSeatYear * o.seatsUsed)} a year</p>}
            </section>
          );
        })}
      </div>
      <p className="text-xs text-muted">Payments are handled by Stripe; eBookMe never sees your card. Seats follow the number of active people automatically.</p>
    </div>
  );
}

/** Scales a picked image to fit 600×200 and returns a PNG data URL (keeps the upload under 100 KB). */
async function logoPng(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = () => rej(new Error("That file isn't an image"));
      i.src = url;
    });
    const s = Math.min(1, 600 / img.width, 200 / img.height);
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(img.width * s));
    c.height = Math.max(1, Math.round(img.height * s));
    c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(url);
  }
}

function Branding({ o, reload }: { o: AdminOverview; reload: () => Promise<void> }) {
  const reloadMe = useReloadMe();
  const c = o.company;
  const [name, setName] = useState(c.name);
  const [displayName, setDisplayName] = useState(c.branding.displayName);
  const [color, setColor] = useState(c.branding.color);
  const [logo, setLogo] = useState<string | null | undefined>(undefined); // undefined = unchanged, null = remove
  const [tz, setTz] = useState(c.settings.timezone);
  const [busy, setBusy] = useState(false);
  const canBrand = o.plan.branding;
  const shownLogo = logo === undefined ? c.branding.logoPng : logo ?? undefined;
  const save = async () => {
    setBusy(true);
    try {
      await api("/admin/company", { body: { name, ...(canBrand ? { branding: { displayName, color, ...(logo !== undefined ? { logo } : {}) } } : {}), settings: { timezone: tz } } });
      toast("Saved");
      setLogo(undefined);
      await Promise.all([reload(), reloadMe()]);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Card title="Branding">
        {!canBrand && (
          <div className="mb-3">
            <ErrorNote>
              Your logo and colour on booking pages and emails are part of the Standard and Teams plans.{" "}
              <Link to="/company/billing" className="underline">
                Choose a plan
              </Link>
            </ErrorNote>
          </div>
        )}
        <div className="space-y-3">
          <label className="block text-sm">
            <span className="block font-medium text-ink-2 mb-1">Company name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </label>
          <fieldset disabled={!canBrand} className="space-y-3 disabled:opacity-60">
            <label className="block text-sm">
              <span className="block font-medium text-ink-2 mb-1">Name clients see</span>
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className={inputCls} />
              <span className="block text-xs text-muted mt-1">“Booked with {displayName || name}”</span>
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-ink-2 mb-1">Brand colour</span>
              <span className="flex gap-2">
                <input type="color" value={color} onChange={(e) => setColor(e.target.value)} aria-label="Brand colour" className="h-9 w-12 rounded border border-line bg-surface" />
                <input value={color} onChange={(e) => setColor(e.target.value)} className={cx(inputCls, "font-mono")} style={{ width: "8rem" }} aria-label="Brand colour hex" />
              </span>
            </label>
            <div className="text-sm">
              <span className="block font-medium text-ink-2 mb-1">Logo</span>
              <div className="flex items-center gap-3">
                <div className="h-14 min-w-28 rounded-lg border border-line bg-surface-2 flex items-center justify-center px-3">{shownLogo ? <CompanyMark name={name} logo={shownLogo} height={36} /> : <span className="text-xs text-muted">No logo</span>}</div>
                <label className={cx("inline-flex items-center gap-1.5 rounded-lg border border-line px-3 h-9 text-sm font-medium", canBrand ? "cursor-pointer hover:bg-surface-2" : "cursor-not-allowed")}>
                  <ImageUp className="size-4" /> Upload
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    hidden
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (f) setLogo(await logoPng(f).catch((x) => (toast((x as Error).message, "error"), undefined)));
                    }}
                  />
                </label>
                {shownLogo && (
                  <Button variant="ghost" onClick={() => setLogo(null)}>
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </fieldset>
          {/* What a client's booking page header looks like */}
          <div className="rounded-xl border border-line overflow-hidden mt-2" aria-label="Booking page preview">
            <div className="h-1" style={{ background: canBrand ? color : "var(--accent)" }} />
            <div className="p-4">
              {canBrand && shownLogo ? <CompanyMark name={displayName || name} logo={shownLogo} height={30} /> : <span className="font-bold">{canBrand ? displayName || name : name}</span>}
              <div className="mt-3 text-sm font-semibold">Intro call · 30 min</div>
              <div className="mt-3 inline-block rounded-lg px-4 py-2 text-sm font-semibold text-white" style={{ background: canBrand ? color : "var(--accent)" }}>
                Confirm booking
              </div>
            </div>
          </div>
        </div>
      </Card>
      <Card title="Defaults">
        <div className="space-y-3 text-sm">
          <label className="block" htmlFor="co-tz">
            <span className="block font-medium text-ink-2 mb-1">Company time zone</span>
          </label>
          <div className="bk" style={{ padding: 0 }}>
            <TimezoneSelect id="co-tz" value={tz} onChange={setTz} />
          </div>
          <span className="block text-xs text-muted">New people start in this time zone, and new links use it until they change their own. Clients always see times in their own time zone.</span>
        </div>
      </Card>
      <div className="lg:col-span-2 flex justify-end">
        <Button variant="primary" busy={busy} onClick={save}>
          Save changes
        </Button>
      </div>
    </div>
  );
}

const AUDIT_LABEL: Record<string, string> = {
  company_created: "Company registered",
  company_updated: "Company settings changed",
  member_invited: "Person invited",
  member_updated: "Person changed",
  member_removed: "Person removed",
  domain_added: "Domain added",
  domain_verified: "Domain verified",
  domain_removed: "Domain removed",
  sso_added: "Single sign-on provider added",
  sso_replaced: "Single sign-on provider replaced",
  sso_removed: "Single sign-on provider removed",
  sso_tested: "Single sign-on tested",
  sso_settings: "Single sign-on settings changed",
  checkout_started: "Checkout started",
  subscription: "Subscription changed",
  platform_update: "Changed by eBookMe",
  auto_suspended: "Paused automatically",
};

function Audit() {
  const { data, error, loading } = useApi<{ entries: { ts: string; actor: string; action: string; detail?: Record<string, unknown> }[] }>("/admin/audit");
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (loading && !data) return <Spinner />;
  return (
    <div className="bg-surface border border-line rounded-xl divide-y divide-line">
      {!data?.entries.length && <Empty icon={<ScrollText className="size-10" />} title="Nothing yet" />}
      {data?.entries.map((e, i) => (
        <div key={i} className="px-4 py-2.5 text-sm flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
          <span className="text-muted whitespace-nowrap sm:w-44">{fullDate(e.ts)}</span>
          <span className="font-medium sm:w-64">{AUDIT_LABEL[e.action] ?? e.action}</span>
          <span className="text-ink-2 truncate">{e.actor}</span>
          {e.detail && (
            <span className="text-xs text-muted truncate">
              {Object.entries(e.detail)
                .map(([k, v]) => `${k}: ${v}`)
                .join(" · ")}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
